/* eslint-disable react-refresh/only-export-components */
 
import { CustomAxiosResponse, setAxiosAuthToken } from "@/configurations/AxiosConfig"
import AuthenticationService from "@/services/AuthenticationService"
import { ILoginForm, IProfileUser } from "@/types/type"
import Helpers from "@/utilities/Helpers"
import LocalStorage from "@/utilities/LocalStorage"
import { createContext, createRef, ReactNode, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { toast } from "sonner"
import { useAbility } from "./AbilityContext"
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next"
import Constants from "@/constants/Constants"

export interface AuthContext {
  isAuthenticated: boolean
  signIn: (data: ILoginForm) => Promise<void>
  logout: () => Promise<void>
  user: IProfileUser | null
  setUser: React.Dispatch<React.SetStateAction<IProfileUser | null>>,
  isLoading: boolean;
  forgotPassword: (email: string) => Promise<boolean>
  resetPassword: (data: string) => Promise<boolean>
  verifyToken: () => Promise<void>
  isSecurity: boolean;
}

 
export const authLogoutRef = createRef<{ logout: () => Promise<void> }>();

const AuthContext = createContext<AuthContext | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<IProfileUser | null>(null)
  const [authToken, setAuthToken] = useState(LocalStorage.token() || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!LocalStorage.token())
  const [isLoading, setIsLoading] = useState(true)
  const { updatePermissions } = useAbility();
  const { t } = useTranslation()
  const isSecurity = user?.roles && user?.roles?.length > 0 ? user?.roles?.some(role => role.id === +Constants.Roles.GüvenlikGorevlisi || role.id === +Constants.Roles.OperasyonGorevlisi) : false;

  const signIn = async (data: ILoginForm) => {
    const response: CustomAxiosResponse<any> = await AuthenticationService.signIn(data)
    if (Helpers.isResponseSuccessful(response)) {
      Sentry.setUser({
        email: response.data.email,
        id: response.data.id,
        first_name: response.data.first_name,
        last_name: response.data.last_name,
      });
      setAuthToken(response.data.access_token)
      LocalStorage.setToken(response.data.access_token)
      setAxiosAuthToken(response.data.access_token)
      setIsAuthenticated(true)
      setUser(response.data);
      updatePermissions(response.data.permissions);
      toast.success(response.message ?? "Giriş başarılı")
    } else {
      setUser(null)
      toast.error(response.message ? t(response.message) : "Bir hata oluştu")
    }
  }

  const clearUserInfo = () => {
    LocalStorage.clearToken();
    LocalStorage.clearUser();
    LocalStorage.clearSelectedCompany();
    setUser(null);
    setAuthToken(null);
    setIsAuthenticated(false);
  };



  const verifyToken = async () => {
    setIsLoading(true)
    if (!authToken) {
      clearUserInfo()
      setIsLoading(false)
      return
    }
    setAxiosAuthToken(authToken)
    const response = await AuthenticationService.validate();
    if (Helpers.isResponseSuccessful(response)) {
      Sentry.setUser({
        email: response.data.email,
        id: response.data.id,
        first_name: response.data.first_name,
        last_name: response.data.last_name,
      });
      setIsAuthenticated(true);
      setUser(response.data);
      updatePermissions(response.data.permissions);
      LocalStorage.setToken(authToken as string)
      setAuthToken(authToken);
    } else if (response && response.status === 401) {
      clearUserInfo();
    } else {
      setIsAuthenticated(false);
    }
    setIsLoading(false)
  };

  // const logoutRequest = async () => {
  //   const response: CustomAxiosResponse<any> = await AuthenticationService.logout()
  //   if (Helpers.isResponseSuccessful(response)) {
  //     toast.success("Çıkış başarılı")
  //   } else {
  //     toast.error(response.message ? t(response.message) : "Bir hata oluştu")
  //   }
  //   return response;
  // }

  const logout = async () => {
    // const response = await logoutRequest();
    // if (Helpers.isResponseSuccessful(response)) {
    LocalStorage.clearSelectedCompany();
    clearUserInfo();
    setAxiosAuthToken(null);
    setTimeout(() => {
      if (document) { document.body.style.pointerEvents = 'unset' } // sidebar içindeki popover açıkken logout yapıldığı için pointerEvents'i unset yapıyoruz yoksa inputlara focus olmuyor
    }, 1);
    // }
  };

  useImperativeHandle(authLogoutRef, () => ({ logout }));

  const forgotPassword = async (email: string) => {
    const response: CustomAxiosResponse<any> = await AuthenticationService.forgotPassword(email)
    if (Helpers.isResponseSuccessful(response)) {
      toast.success(response.message ?? "Şifre sıfırlama talebiniz alınmıştır.")
      return true
    } else {
      toast.error(response?.message ?? "Bir hata oluştu")
      return false
    }
  }

  const resetPassword = async (data: string) => {
    const response: CustomAxiosResponse<any> = await AuthenticationService.resetPassword(data)
    if (Helpers.isResponseSuccessful(response)) {
      toast.success(response.message ?? "Şifreniz başarıyla sıfırlandı.")
      return true
    } else {
      toast.error(response?.message ?? "Bir hata oluştu")
      return false
    }
  }
  const updateUser = (newUser: IProfileUser | null) => {
    setUser(newUser);
  }

  useEffect(() => {
    if (!authToken) {
      setIsAuthenticated(false)
      setUser(null)
      setIsLoading(false)
      return;
    }
    verifyToken();
  }, [authToken]);


  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      setUser,
      signIn,
      logout,
      isLoading,
      forgotPassword,
      resetPassword,
      verifyToken,
      updateUser,
      isSecurity
    }), [isAuthenticated, user, isLoading, isSecurity])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}